import { Card, Container, Badge, Table, ButtonGroup, Button } from 'react-bootstrap';
import type { CaptaincyRecord } from './captaincy.utils';
import { calculateCaptaincyRecord } from './captaincy.utils';
import type { CaptaincyViewMode } from './captaincy-filters.utils';

interface CaptaincyStatsProps {
  records: CaptaincyRecord[];
  viewMode: CaptaincyViewMode;
  onViewModeChange: (mode: CaptaincyViewMode) => void;
}

type ViewMode = CaptaincyViewMode;

// Component for rendering a single captaincy record row
const CaptaincyRecordRow = ({ record, viewMode }: { record: CaptaincyRecord; viewMode: ViewMode }) => {
  const { wins, losses } = calculateCaptaincyRecord(record.captaincies);

  return (
    <tr key={record.memberId}>
      <td>
        <strong>{record.memberName}</strong>
        <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
          Record: {wins}W - {losses}L
        </div>
      </td>
      <td className="text-center">{record.timesAsCaptain}</td>
      <td>
        {record.captaincies.map((captaincy, index) => (
          <div key={index} style={{ marginBottom: '4px' }}>
            <span style={{ marginRight: '8px' }}>{captaincy.year}</span>
            <Badge className={captaincy.team === 'EUR' ? 'team-eur' : 'team-usa'} style={{ marginRight: '8px' }}>
              {captaincy.team === 'EUR' ? 'Europe' : 'USA'}
            </Badge>
            <span style={{ color: captaincy.won ? '#28a745' : '#dc3545', fontWeight: 'bold' }}>
              {captaincy.won ? 'Won' : 'Lost'}
            </span>
          </div>
        ))}
      </td>
      <td className="text-center">
        {record.isCurrentCaptain ? (
          <Badge bg="success">{viewMode === 'due' ? 'Current' : 'Last'}</Badge>
        ) : (
          <span>
            {viewMode === 'due'
              ? record.appearancesSinceLastCaptaincyIncludingUpcoming
              : record.appearancesSinceLastCaptaincy}
          </span>
        )}
      </td>
    </tr>
  );
};

// Header component
const CaptaincyHeader = ({
  viewMode,
  onViewModeChange,
}: {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
}) => (
  <Card.Header>
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '1rem',
      }}
    >
      <h2 style={{ margin: 0, color: 'white' }}>Ryd-Her Cup Captaincy</h2>
      <ButtonGroup>
        <Button
          variant={viewMode === 'record' ? 'light' : 'outline-light'}
          onClick={() => onViewModeChange('record')}
          style={{ fontWeight: viewMode === 'record' ? 'bold' : 'normal' }}
        >
          Record
        </Button>
        <Button
          variant={viewMode === 'due' ? 'light' : 'outline-light'}
          onClick={() => onViewModeChange('due')}
          style={{ fontWeight: viewMode === 'due' ? 'bold' : 'normal' }}
        >
          Due Captaincy
        </Button>
      </ButtonGroup>
    </div>
  </Card.Header>
);

export default function CaptaincyStats({ records, viewMode, onViewModeChange }: CaptaincyStatsProps) {
  if (!records || records.length === 0) {
    return (
      <Container>
        <Card>
          <Card.Header>
            <h2>Ryd-Her Cup Captaincy Records</h2>
          </Card.Header>
          <Card.Body>
            <p>No captaincy records found.</p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <CaptaincyHeader viewMode={viewMode} onViewModeChange={onViewModeChange} />
        <Card.Body>
          <p>
            This table shows the captaincy history for all members who have captained a team in the Ryd-Her Cup
            competition.
          </p>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Captain</th>
                <th className="text-center">Times as Captain</th>
                <th>Years (Team)</th>
                <th className="text-center">Appearances Since Last Captaincy</th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <CaptaincyRecordRow key={record.memberId} record={record} viewMode={viewMode} />
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </Container>
  );
}
